import { useCallback, useEffect, useRef, useState } from 'react';
import { Room, RoomEvent } from 'livekit-client';
import { cancelRealtimeSession } from '../../services/Interviews/chatbotService';

const KEEP_ALIVE_INTERVAL_MS = 60000;
const VIDEO_MOUNT_RETRY_MS = 100;
const MAX_VIDEO_MOUNT_RETRIES = 20;

// Single place that touches livekit-client and the LiveAvatar websocket.
// status: 'idle' | 'connecting' | 'ready' | 'speaking' | 'error' | 'stopped'
// All methods are safe to call: they never throw to callers.
export default function useLiveAvatar() {
  const [status, setStatus] = useState('idle');
  // True when the browser blocked audio autoplay (strict-autoplay policies,
  // e.g. Safari): the UI should offer a tap-to-unmute affordance that calls
  // resumeAudio() from a user gesture.
  const [needsAudioStart, setNeedsAudioStart] = useState(false);

  const videoRef = useRef(null);
  const roomRef = useRef(null);
  const wsRef = useRef(null);
  // The component-owned <video> track: detach only, never remove the element.
  const videoTrackRef = useRef(null);
  // Audio tracks attach() their own <audio> elements which we append to the
  // document, so those elements must be removed again on cleanup.
  const audioTracksRef = useRef([]);
  const keepAliveTimerRef = useRef(null);
  const videoRetryTimerRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const pendingChunksRef = useRef(null);
  const wsReadyRef = useRef(false);
  const startedRef = useRef(false);
  const eventIdRef = useRef(0);
  const statusRef = useRef('idle');
  // Generation token: bumping it invalidates any in-flight async work
  // (connect promises, ws handlers, video mount retries).
  const genRef = useRef(0);

  const setStatusSafe = useCallback((next) => {
    statusRef.current = next;
    setStatus(next);
  }, []);

  const sendJson = useCallback((payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(JSON.stringify(payload));
      return true;
    } catch (error) {
      console.error('Failed to send LiveAvatar websocket message.', error);
      return false;
    }
  }, []);

  // Send one full utterance: interrupt anything queued server-side, then the
  // audio chunks in order, then speak_end to flush.
  const sendUtterance = useCallback(
    (chunks) => {
      sendJson({ type: 'agent.interrupt' });
      chunks.forEach((chunk) => {
        sendJson({ type: 'agent.speak', audio: chunk });
      });
      sendJson({
        type: 'agent.speak_end',
        event_id: String(eventIdRef.current++),
      });
    },
    [sendJson],
  );

  const attachVideoTrack = useCallback((track, gen, retries) => {
    if (gen !== genRef.current) return;
    const video = videoRef.current;
    if (!video) {
      // The <video> element may not be mounted yet (it renders once the
      // hook reports 'connecting'). Retry briefly instead of failing.
      if (retries >= MAX_VIDEO_MOUNT_RETRIES) {
        console.error('LiveAvatar video element never mounted.');
        return;
      }
      videoRetryTimerRef.current = setTimeout(
        () => attachVideoTrack(track, gen, retries + 1),
        VIDEO_MOUNT_RETRY_MS,
      );
      return;
    }
    try {
      track.attach(video);
      videoTrackRef.current = track;
    } catch (error) {
      console.error('Failed to attach LiveAvatar video track.', error);
    }
  }, []);

  // Tear down everything this hook created. Does NOT cancel the backend
  // session — stop() decides whether that is needed.
  const teardown = useCallback(() => {
    if (keepAliveTimerRef.current) {
      clearInterval(keepAliveTimerRef.current);
      keepAliveTimerRef.current = null;
    }
    if (videoRetryTimerRef.current) {
      clearTimeout(videoRetryTimerRef.current);
      videoRetryTimerRef.current = null;
    }
    wsReadyRef.current = false;
    pendingChunksRef.current = null;
    const ws = wsRef.current;
    wsRef.current = null;
    if (ws) {
      try {
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        ws.close();
      } catch (error) {
        console.error('Failed to close LiveAvatar websocket.', error);
      }
    }
    const videoTrack = videoTrackRef.current;
    videoTrackRef.current = null;
    if (videoTrack) {
      try {
        // Detach media only — the <video> element belongs to the component.
        videoTrack.detach();
      } catch (error) {
        console.error('Failed to detach LiveAvatar video track.', error);
      }
    }
    const audioTracks = audioTracksRef.current;
    audioTracksRef.current = [];
    audioTracks.forEach((track) => {
      try {
        track.detach().forEach((element) => element.remove());
      } catch (error) {
        console.error('Failed to detach LiveAvatar audio track.', error);
      }
    });
    const room = roomRef.current;
    roomRef.current = null;
    if (room) {
      try {
        Promise.resolve(room.disconnect()).catch((error) => {
          console.error('Failed to disconnect LiveKit room.', error);
        });
      } catch (error) {
        console.error('Failed to disconnect LiveKit room.', error);
      }
    }
  }, []);

  const start = useCallback(
    async (connection) => {
      if (!connection) return;
      // Ref-guard against double start (React StrictMode double effects).
      if (startedRef.current) return;
      startedRef.current = true;
      genRef.current += 1;
      const gen = genRef.current;
      setStatusSafe('connecting');
      setNeedsAudioStart(false);
      sessionTokenRef.current = connection.session_token || null;

      try {
        const room = new Room();
        roomRef.current = room;
        room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
          if (gen !== genRef.current) return;
          setNeedsAudioStart(!room.canPlaybackAudio);
        });
        room.on(RoomEvent.TrackSubscribed, (track) => {
          if (gen !== genRef.current) return;
          try {
            if (track.kind === 'video') {
              attachVideoTrack(track, gen, 0);
            } else if (track.kind === 'audio') {
              const element = track.attach();
              element.style.display = 'none';
              document.body.appendChild(element);
              audioTracksRef.current.push(track);
            }
          } catch (error) {
            console.error('Failed to handle subscribed track.', error);
          }
        });

        await room.connect(
          connection.livekit_url,
          connection.livekit_client_token,
        );
        if (gen !== genRef.current) return;

        const ws = new WebSocket(connection.ws_url);
        wsRef.current = ws;

        ws.onmessage = (event) => {
          if (gen !== genRef.current) return;
          let message = null;
          try {
            message = JSON.parse(event.data);
          } catch (error) {
            return;
          }
          const type =
            message?.type || message?.event_type || message?.event || '';
          if (type === 'session.state_updated') {
            const state = message?.state || message?.data?.state;
            if (state === 'connected' && !wsReadyRef.current) {
              wsReadyRef.current = true;
              if (statusRef.current === 'connecting') {
                setStatusSafe('ready');
              }
              const pending = pendingChunksRef.current;
              pendingChunksRef.current = null;
              if (pending && pending.length > 0) {
                sendUtterance(pending);
              }
            }
          } else if (type === 'agent.speak_started') {
            setStatusSafe('speaking');
          } else if (type === 'agent.speak_ended') {
            setStatusSafe('ready');
          }
        };

        ws.onerror = (event) => {
          if (gen !== genRef.current) return;
          console.error('LiveAvatar websocket error.', event);
          setStatusSafe('error');
        };

        // Handlers are nulled before an intentional close, so reaching this
        // handler means the socket died unexpectedly.
        ws.onclose = () => {
          if (gen !== genRef.current) return;
          console.error('LiveAvatar websocket closed unexpectedly.');
          setStatusSafe('error');
        };

        // Periodic keep-alive extends LiveAvatar's idle timeout while the
        // candidate reads the question / records an answer.
        keepAliveTimerRef.current = setInterval(() => {
          sendJson({
            type: 'session.keep_alive',
            event_id: String(eventIdRef.current++),
          });
        }, KEEP_ALIVE_INTERVAL_MS);
      } catch (error) {
        console.error('Failed to start LiveAvatar session.', error);
        if (gen !== genRef.current) return;
        teardown();
        setStatusSafe('error');
      }
    },
    [attachVideoTrack, sendJson, sendUtterance, setStatusSafe, teardown],
  );

  const speak = useCallback(
    (chunks) => {
      if (!chunks || chunks.length === 0) return;
      try {
        const ws = wsRef.current;
        if (
          !wsReadyRef.current ||
          !ws ||
          ws.readyState !== WebSocket.OPEN
        ) {
          // Not connected yet: queue this utterance (replacing any previous
          // one) — it flushes when the session reports 'connected'.
          pendingChunksRef.current = chunks;
          return;
        }
        sendUtterance(chunks);
      } catch (error) {
        console.error('Failed to queue LiveAvatar speech.', error);
      }
    },
    [sendUtterance],
  );

  // Must be called from a user gesture (button click) when needsAudioStart
  // is true — resumes audio playback blocked by the browser's autoplay policy.
  const resumeAudio = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;
    try {
      Promise.resolve(room.startAudio())
        .then(() => setNeedsAudioStart(false))
        .catch((error) => {
          console.error('Failed to resume LiveAvatar audio.', error);
        });
    } catch (error) {
      console.error('Failed to resume LiveAvatar audio.', error);
    }
  }, []);

  const stop = useCallback(() => {
    genRef.current += 1;
    startedRef.current = false;
    const sessionToken = sessionTokenRef.current;
    sessionTokenRef.current = null;
    teardown();
    if (sessionToken) {
      // Fire-and-forget, best-effort backend cancel (service swallows errors).
      cancelRealtimeSession({ params: { sessionToken } });
    }
    setNeedsAudioStart(false);
    setStatusSafe('stopped');
  }, [setStatusSafe, teardown]);

  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  return { status, needsAudioStart, videoRef, start, speak, stop, resumeAudio };
}
